import React, { useState, useEffect, useCallback } from 'react';
import { X, Edit2, Trash2, Calendar, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { TimelineItem, CreateTimelineRequest } from '../../types';
import { timelineAPI } from '../../services/api';
import toast from 'react-hot-toast';

interface TimelineModalProps {
  isOpen: boolean;
  onClose: () => void;
  publicationId: number;
  publicationStage?: string;
  onTimelineUpdated: () => void;
}

const TimelineModal: React.FC<TimelineModalProps> = ({
  isOpen,
  onClose,
  publicationId,
  publicationStage,
  onTimelineUpdated
}) => {
  const [timeline, setTimeline] = useState<TimelineItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingItem, setEditingItem] = useState<TimelineItem | null>(null);
  const [formData, setFormData] = useState<CreateTimelineRequest>({
    title: '',
    description: '',
    due_date: ''
  });

  const loadTimeline = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await timelineAPI.getTimeline(publicationId);
      setTimeline(data);
    } catch (error: any) {
      console.error('Error loading timeline:', error);
      toast.error('Failed to load timeline');
    } finally {
      setIsLoading(false);
    }
  }, [publicationId]);

  useEffect(() => {
    if (isOpen) {
      loadTimeline();
    }
  }, [isOpen, loadTimeline]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    setIsSubmitting(true);
    
    try {
      if (editingItem) {
        await timelineAPI.updateTimelineItem(editingItem.id, formData);
        toast.success('Timeline item updated successfully');
      } else {
        await timelineAPI.createTimelineItem(publicationId, formData);
        toast.success('Timeline item created successfully');
      }
      
      // Clear cache to ensure fresh data
      const { cacheService } = await import('../../services/cache');
      cacheService.clearPattern(`/publications/${publicationId}/batch`);
      cacheService.clearPattern('/dashboard/batch');
      cacheService.clearPattern('/admin/batch');
      
      setFormData({ title: '', description: '', due_date: '' });
      setEditingItem(null);
      loadTimeline();
      onTimelineUpdated();
    } catch (error: any) {
      console.error('Error saving timeline item:', error);
      toast.error(error.response?.data?.error || 'Failed to save timeline item');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (item: TimelineItem) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      description: item.description || '',
      due_date: item.due_date || ''
    });
  };

  const handleCancel = () => {
    setEditingItem(null);
    setFormData({ title: '', description: '', due_date: '' });
  };

  const handleDelete = async (itemId: number) => {
    if (!window.confirm('Are you sure you want to delete this timeline item?')) {
      return;
    }

    try {
      await timelineAPI.deleteTimelineItem(itemId);
      
      // Clear cache to ensure fresh data
      const { cacheService } = await import('../../services/cache');
      cacheService.clearPattern(`/publications/${publicationId}/batch`);
      cacheService.clearPattern('/dashboard/batch');
      cacheService.clearPattern('/admin/batch');
      
      toast.success('Timeline item deleted successfully');
      loadTimeline();
      onTimelineUpdated();
    } catch (error: any) {
      console.error('Error deleting timeline item:', error);
      toast.error('Failed to delete timeline item');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'in_progress':
        return <Clock className="h-4 w-4 text-blue-600" />;
      case 'overdue':
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Project Timeline</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Add/Edit Form */}
          <form onSubmit={handleSubmit} className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title *
                </label>
                {(publicationStage === '3' || publicationStage === '4') ? (
                  <select
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="">Select a title...</option>
                    <option value="Main messages, figures, and tables">Main messages, figures, and tables</option>
                    <option value="Draft completed">Draft completed</option>
                    <option value="Internal review">Internal review</option>
                    <option value="External review">External review</option>
                    <option value="Submission">Submission</option>
                    <option value="Revision">Revision</option>
                  </select>
                ) : (
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="Enter timeline item title"
                  />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Due Date
                </label>
                <input
                  type="date"
                  name="due_date"
                  value={formData.due_date}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter description (optional)"
              />
            </div>
            <div className="flex justify-end space-x-3">
              {editingItem && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? 'Saving...' : editingItem ? 'Update' : 'Add Timeline Item'}
              </button>
            </div>
          </form>

          {/* Timeline List */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Timeline Items</h3>
            {isLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading timeline...</p>
              </div>
            ) : timeline.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No timeline items yet. Add one above to get started.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {timeline.map((item) => (
                  <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          {getStatusIcon(item.status)}
                          <h4 className="font-medium text-gray-900">{item.title}</h4>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(item.status)}`}>
                            {item.status.replace('_', ' ')}
                          </span>
                        </div>
                        {item.description && (
                          <p className="text-sm text-gray-600 mb-2">{item.description}</p>
                        )}
                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                          {item.due_date && (
                            <div className="flex items-center space-x-1">
                              <Calendar className="h-3 w-3" />
                              <span>Due: {new Date(item.due_date).toLocaleDateString()}</span>
                            </div>
                          )}
                          <span>Created by: {item.created_by_name}</span>
                          <span>Created: {new Date(item.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <button
                          onClick={() => handleEdit(item)}
                          className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                          title="Edit timeline item"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          title="Delete timeline item"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimelineModal;
